import { skipToken, useQuery } from '@tanstack/react-query';

import { type Skills } from '@air/api';
import { type Succeeded } from '@air/server-sdk';
import { isAirrorInstance } from '@air/utils-error';

import { skillsListWithCanvasType } from './skillsCanvasApi';

export const SKILLS_KEY = 'SKILLS';

const FIVE_MINUTES_MS = 300_000;

type Skill = Succeeded<typeof Skills.list>['data'][number];

export type SkillScope = Skill['scope'];

export type SkillsKey = [typeof SKILLS_KEY, string, { scope?: SkillScope; type: 'canvas' }];

export const getSkillsKey = (workspaceId: string, scope?: SkillScope): SkillsKey => [
  SKILLS_KEY,
  workspaceId,
  { scope, type: 'canvas' },
];

export type UseGetSkillsParams = {
  workspaceId: string | undefined;
  scope?: SkillScope;
};

export type UseGetSkillsOptions = {
  enabled?: boolean;
};

export const useGetSkills = ({ workspaceId, scope }: UseGetSkillsParams, options?: UseGetSkillsOptions) => {
  const queryFn = workspaceId
    ? async () => {
        const result = await skillsListWithCanvasType({ scope, workspaceId });

        if (isAirrorInstance(result)) {
          throw result;
        }

        return result.data;
      }
    : skipToken;

  return useQuery({
    enabled: !!workspaceId && options?.enabled,
    queryFn,
    queryKey: getSkillsKey(workspaceId ?? '', scope),
    staleTime: FIVE_MINUTES_MS,
  });
};
